import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/auth/apiClient';
import { queryKeys } from './queryKeys';
import type { Foerderung32Entry, I18nEntry, SvBeitragEntry, TvoedEntry } from './types';

export function useTvoed() {
  return useQuery({
    queryKey: queryKeys.admin.tvoed(),
    queryFn: async (): Promise<TvoedEntry[]> => {
      const res = await apiClient.get<TvoedEntry[]>('/admin/tvoed');
      return res.data;
    },
  });
}

export function useSvBeitraege() {
  return useQuery({
    queryKey: queryKeys.admin.sv(),
    queryFn: async (): Promise<SvBeitragEntry[]> => {
      const res = await apiClient.get<SvBeitragEntry[]>('/admin/sv-beitraege');
      return res.data;
    },
  });
}

export function useFoerderung32Refdata() {
  return useQuery({
    queryKey: queryKeys.admin.foerderung32(),
    queryFn: async (): Promise<Foerderung32Entry[]> => {
      const res = await apiClient.get<Foerderung32Entry[]>('/admin/foerderung-32');
      return res.data;
    },
  });
}

export function useI18nEntries() {
  return useQuery({
    queryKey: queryKeys.admin.i18n(),
    queryFn: async (): Promise<I18nEntry[]> => {
      const res = await apiClient.get<I18nEntry[]>('/admin/i18n');
      return res.data;
    },
  });
}

export function useUpdateI18nEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entry: I18nEntry): Promise<I18nEntry> => {
      const res = await apiClient.put<I18nEntry>('/admin/i18n', entry);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.admin.i18n() });
    },
  });
}

/** Triggert einen DeepL-Übersetzungsvorschlag (mit Glossar-Schutz im Backend). */
export function useDeepLSuggestion() {
  return useMutation({
    mutationFn: async (params: {
      namespace: string;
      key: string;
      sourceLang: string;
      targetLang: string;
      sourceValue: string;
    }): Promise<{ suggestion: string }> => {
      const res = await apiClient.post<{ suggestion: string }>('/admin/i18n/suggest', params);
      return res.data;
    },
  });
}

export interface AuditEntry {
  id: string;
  ts: string;
  user_id: string;
  action: string;
  before: unknown;
  after: unknown;
  traeger_id: string | null;
  plan_id: string | null;
}

export interface AuditPage {
  total: number;
  limit: number;
  offset: number;
  items: AuditEntry[];
}

export function useAuditLog(
  params: { limit?: number; offset?: number; user?: string; action?: string } = {},
) {
  return useQuery({
    queryKey: ['admin', 'audit', params],
    queryFn: async (): Promise<AuditPage> => {
      const res = await apiClient.get<AuditPage>('/admin/audit', { params });
      return res.data;
    },
  });
}
